<template>
  <div class="iabf-dancer-theme register-page">
    <div class="register-col">
      <img
        v-if="logoUrl && !logoFailed"
        class="celebrate-logo"
        :src="logoUrl"
        :alt="logoAlt"
        @error="logoFailed = true"
      />
      <p class="iabf-caption">IABF {{ registrationYear }}</p>
      <h1 class="iabf-display">Welcome to IABF {{ registrationYear }} — you’re in!</h1>
      <p class="celebrate-lead">
        We can’t wait to see you. Your spot is reserved, and this is going to be a beautiful week.
      </p>

      <div class="celebrate-card">
        <p v-if="fromCredit" class="iabf-body">
          Your held deposit covered today — no charge just now.
        </p>
        <p v-else class="iabf-body">
          Your {{ depositLabel }} deposit is in.
        </p>
        <p v-if="fromCredit && cardSetupOk" class="iabf-hint" style="margin-top: 12px">
          Card on file — we’ll use it for the monthly payments starting in January.
        </p>
        <p v-else-if="fromCredit && cardSetupSkipped" class="iabf-hint" style="margin-top: 12px">
          You can add a card anytime under Billing. We’ll need one for the monthly payments.
        </p>
        <p class="iabf-hint" style="margin-top: 12px">
          Watch your inbox — we sent a little welcome note.
        </p>
      </div>

      <p v-if="error && authState === 'signed_in'" class="error-banner" role="alert">{{ error }}</p>
      <p v-if="successNote" class="success-banner">{{ successNote }}</p>
      <p v-if="resetSent" class="success-banner">Check your inbox — we sent a reset link.</p>

      <section v-if="authState === 'signed_in'" class="iabf-stack">
        <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" @click="goToDashboard">
          Open your dashboard →
        </button>
      </section>

      <section v-else-if="authState === 'has_account'" class="iabf-stack">
        <h2 class="iabf-question" style="font-size: 22px">One more step</h2>
        <p class="iabf-hint">
          Enter your IABF or Broche Ballet Online password to open your dashboard.
        </p>
        <div class="iabf-field">
          <label class="iabf-label" for="suc-email">Email</label>
          <input id="suc-email" v-model.trim="email" class="iabf-input" type="email" autocomplete="email" />
        </div>
        <div class="iabf-field">
          <label class="iabf-label" for="suc-pass">IABF or Broche Ballet Online password</label>
          <input id="suc-pass" v-model="password" class="iabf-input" type="password" autocomplete="current-password" @keyup.enter="signIn" />
        </div>
        <p v-if="error" class="error-banner error-banner--cta" role="alert">{{ error }}</p>
        <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" :disabled="busy" @click="signIn">
          {{ busy ? 'Signing in…' : 'Log in to your dashboard' }}
        </button>
        <p class="forgot-link">
          <a href="#" @click.prevent="sendReset">Forgot your password?</a>
        </p>
      </section>

      <section v-else-if="authState === 'new_account'" class="iabf-stack">
        <h2 class="iabf-question" style="font-size: 22px">Set a password</h2>
        <p class="iabf-hint">
          Then hop into your dashboard and start planning your ballet vacation.
        </p>
        <div class="iabf-field">
          <label class="iabf-label" for="suc-email2">Email</label>
          <input id="suc-email2" v-model.trim="email" class="iabf-input" type="email" autocomplete="email" />
        </div>
        <div class="iabf-field">
          <label class="iabf-label" for="suc-pass2">Create password</label>
          <input id="suc-pass2" v-model="password" class="iabf-input" type="password" autocomplete="new-password" />
        </div>
        <div class="iabf-field">
          <label class="iabf-label" for="suc-pass3">Confirm password</label>
          <input id="suc-pass3" v-model="confirm" class="iabf-input" type="password" autocomplete="new-password" @keyup.enter="signUp" />
        </div>
        <p v-if="error" class="error-banner error-banner--cta" aria-hidden="true">{{ error }}</p>
        <button type="button" class="iabf-btn iabf-btn--primary iabf-btn--block" :disabled="busy" @click="signUp">
          {{ busy ? 'Saving…' : 'Save password & open dashboard' }}
        </button>
      </section>

      <section v-else class="iabf-stack">
        <p class="iabf-hint">Just a moment…</p>
      </section>

      <p class="iabf-caption" style="margin-top: 28px">
        Questions? Email <a href="mailto:hello@iabf.dance">hello@iabf.dance</a>
      </p>
    </div>
  </div>
</template>

<script>
import { auth } from '@/firebase'
import { lookupRegistrationEmail } from '@/utils/iabf-registration-api'
import { getIabfBrand, IABF_BRAND_YEAR } from '@/config/iabf-brand'
import {
  REGISTRATION_YEAR,
  DEPOSIT_CENTS,
  formatUsdFromCents
} from '@/config/iabf-registration-2027'

const SUCCESS_AUTH_KEY = 'iabf-reg-2027-success-auth'

export default {
  name: 'IABFRegisterSuccess',
  data () {
    return {
      registrationYear: REGISTRATION_YEAR,
      depositLabel: formatUsdFromCents(DEPOSIT_CENTS),
      fromCredit: false,
      cardSetupOk: false,
      cardSetupSkipped: false,
      knownHasAuth: false,
      email: '',
      password: '',
      confirm: '',
      authState: 'loading',
      busy: false,
      error: '',
      successNote: '',
      resetSent: false,
      logoFailed: false
    }
  },
  computed: {
    brand () {
      return getIabfBrand(IABF_BRAND_YEAR)
    },
    logoUrl () {
      return this.brand.logoUrl || null
    },
    logoAlt () {
      return this.brand.logoAlt || ('IABF ' + this.registrationYear)
    }
  },
  async created () {
    this.$store.commit('setLayout', 'iabf-layout')
    this.fromCredit = this.$route.query.credit === '1'
    this.cardSetupOk = this.$route.query.card_setup === '1'
    this.cardSetupSkipped = this.fromCredit && this.$route.query.card_setup === '0'
    this.knownHasAuth = false
    try {
      const draft = sessionStorage.getItem('iabf-reg-2027-draft')
      if (draft) {
        const d = JSON.parse(draft)
        if (d.email) this.email = d.email
        if (d.hasAuth) this.knownHasAuth = true
      }
    } catch (e) { /* ignore */ }
    try {
      const saved = sessionStorage.getItem(SUCCESS_AUTH_KEY)
      if (saved) {
        const d = JSON.parse(saved)
        if (d.email && !this.email) this.email = d.email
        if (d.hasAuth) this.knownHasAuth = true
      }
    } catch (e) { /* ignore */ }

    if (this.$route.query.email) {
      this.email = String(this.$route.query.email)
    }
    if (this.$route.query.hasAuth === '1') {
      this.knownHasAuth = true
    }

    await this.confirmReturnedPayment()
    await this.detectAuth()
  },
  methods: {
    async confirmReturnedPayment () {
      const q = this.$route.query || {}
      let enrollmentId = String(q.enrollmentId || '')
      if (!enrollmentId) {
        try {
          enrollmentId = sessionStorage.getItem('iabf-reg-2027-enrollment') || ''
        } catch (e) { /* ignore */ }
      }
      const paymentIntentId = String(q.payment_intent || '')
      const setupIntentId = String(q.setup_intent || '')
      const redirectStatus = String(q.redirect_status || '')
      if (redirectStatus === 'failed' || redirectStatus === 'canceled') {
        this.error = 'That confirmation did not go through. You can go back to registration and try again.'
        return
      }
      const local = typeof window !== 'undefined' &&
        /localhost|127\.0\.0\.1/.test(window.location.hostname)
      const cf = local
        ? 'http://127.0.0.1:5001/broche-ballet-online/us-central1'
        : 'https://us-central1-broche-ballet-online.cloudfunctions.net'
      try {
        if (paymentIntentId && enrollmentId) {
          await fetch(cf + '/confirmRegistrationDeposit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enrollmentId, paymentIntentId })
          })
          try { sessionStorage.removeItem('iabf-reg-2027-draft') } catch (e) { /* ignore */ }
        }
        if (setupIntentId) {
          await fetch(cf + '/confirmCardSetup', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ setupIntentId })
          })
          this.fromCredit = true
          this.cardSetupOk = true
          this.cardSetupSkipped = false
        }
      } catch (e) {
        console.warn('confirm returned payment', e)
      }
    },
    async detectAuth () {
      this.error = ''
      const email = this.email.trim().toLowerCase()
      if (!email) {
        this.authState = this.knownHasAuth ? 'has_account' : 'new_account'
        return
      }
      this.email = email
      const signedIn = auth.currentUser &&
        String(auth.currentUser.email || '').trim().toLowerCase() === email
      if (signedIn) {
        this.authState = 'signed_in'
        return
      }
      try {
        const [methods, lookup] = await Promise.all([
          auth.fetchSignInMethodsForEmail(email).catch(() => []),
          lookupRegistrationEmail(email).catch(() => ({ data: {} }))
        ])
        const data = (lookup && lookup.data) || {}
        const hasAuth = !!(this.knownHasAuth || data.hasAuth || (methods && methods.length))
        this.authState = hasAuth ? 'has_account' : 'new_account'
      } catch (e) {
        this.authState = this.knownHasAuth ? 'has_account' : 'new_account'
      }
    },
    goToDashboard () {
      this.$router.push('/')
    },
    async sendReset () {
      this.error = ''
      this.resetSent = false
      const email = this.email.trim().toLowerCase()
      if (!email) {
        this.error = 'Add your email above and we’ll send a reset link.'
        return
      }
      try {
        const { functions } = await import('@/firebase')
        const sendReset = functions.httpsCallable('iabfSendPasswordReset')
        await sendReset({ email })
        this.resetSent = true
      } catch (e) {
        this.error = (e && e.message) || 'Could not send a reset email. Please try again, or write hello@iabf.dance.'
      }
    },
    async signIn () {
      this.error = ''
      if (!this.email || !this.password) {
        this.error = 'Enter your email and password.'
        return
      }
      this.busy = true
      try {
        await this.$store.dispatch('signInIABFDancer', {
          email: this.email.trim().toLowerCase(),
          password: this.password
        })
        this.successNote = 'Signed in. Opening dashboard…'
        this.$router.push('/')
      } catch (e) {
        this.error = (e && e.message) || 'Sign-in failed.'
      }
      this.busy = false
    },
    async signUp () {
      this.error = ''
      if (!this.email || !this.password) {
        this.error = 'Enter your email and a password.'
        return
      }
      if (this.password !== this.confirm) {
        this.error = 'Passwords do not match.'
        return
      }
      if (this.password.length < 6) {
        this.error = 'Password must be at least 6 characters.'
        return
      }
      this.busy = true
      try {
        await this.$store.dispatch('signUpIABFDancer', {
          email: this.email.trim().toLowerCase(),
          password: this.password
        })
        this.successNote = 'Account created. Opening dashboard…'
        this.$router.push('/')
      } catch (e) {
        const msg = (e && e.message) || ''
        if (/already exists|already in use/i.test(msg)) {
          this.authState = 'has_account'
          this.error = 'You already have a Broche Ballet On-Demand login. Use that password — or reset it below.'
        } else {
          this.error = msg || 'Could not create account.'
        }
      }
      this.busy = false
    }
  }
}
</script>

<style scoped>
.register-page {
  min-height: 70vh;
  padding: 32px 20px 64px;
  background: var(--iabf-bg, #f7f6f4);
}
.register-col {
  max-width: 480px;
  margin: 0 auto;
}
.celebrate-logo {
  display: block;
  height: 72px;
  width: auto;
  max-width: 180px;
  object-fit: contain;
  object-position: left center;
  margin: 0 0 20px;
}
.celebrate-lead {
  margin: 8px 0 20px;
  font-size: 20px;
  line-height: 1.4;
  color: var(--iabf-ink, #1c1b1f);
}
.celebrate-card {
  background: var(--iabf-bg-elevated, #fff);
  border-radius: 16px;
  padding: 20px 20px 18px;
  margin: 0 0 28px;
  box-shadow: 0 1px 0 rgba(28, 27, 31, 0.04);
}
.forgot-link {
  margin: 10px 0 0;
  text-align: center;
  font-size: 15px;
}
.error-banner {
  background: #fff;
  border: 1.5px solid var(--iabf-danger, #b54a4a);
  border-radius: 12px;
  padding: 12px 14px;
  color: var(--iabf-danger, #b54a4a);
  margin: 0 0 16px;
  font-size: 15px;
}
.error-banner--cta {
  margin: 16px 0 12px;
}
.success-banner {
  background: #fff;
  border: 1.5px solid var(--iabf-primary, #8a957d);
  border-radius: 12px;
  padding: 12px 14px;
  color: var(--iabf-ink, #1c1b1f);
  margin: 0 0 16px;
  font-size: 15px;
}
a {
  color: var(--iabf-primary, #8a957d);
}
</style>
